import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Home, Building2, MapPin, TrendingUp, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Landing() {
  const navigate = useNavigate();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [builderProjects, setBuilderProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFeaturedProperties();
    fetchBuilderProjects();
  }, []);

  const fetchFeaturedProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties?limit=6`);
      setFeaturedProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchBuilderProjects = async () => {
    try {
      const response = await axios.get(`${API}/builder-projects?limit=3&approved_only=true`);
      setBuilderProjects(response.data);
    } catch (error) {
      console.error('Error fetching builder projects:', error);
    }
  };

  const handleSearch = () => {
    navigate(`/properties?search=${searchQuery}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1615451057331-4ad3b8c32b83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxrb2NoaSUyMGNpdHklMjB2aWV3fGVufDB8fHx8MTc2OTkyNjM0OXww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 gradient-overlay"></div>
        </div>

        <div className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="max-w-3xl text-white fade-in">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5" data-testid="hero-heading">
              Find Your Perfect Property in Kerala
            </h1>
            <p className="text-base md:text-lg leading-relaxed mb-6 text-white/90">
              Your trusted bridge to buying, selling, and renting properties across all districts of Kerala.
            </p>

            {/* Search Bar */}
            <div className="flex gap-3 max-w-2xl" data-testid="search-bar">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search by location, property type..."
                  className="pl-12 h-14 bg-white border-0 shadow-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button
                data-testid="search-button"
                onClick={handleSearch}
                className="h-14 px-8 bg-primary hover:bg-primary/90 text-white font-medium shadow-lg"
              >
                Search
              </Button>
            </div>

            {/* Quick Action Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <Button
                data-testid="action-buy"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-primary border-0 h-14"
                onClick={() => navigate('/properties?listing_type=sale')}
              >
                Buy a Property
              </Button>
              <Button
                data-testid="action-sell"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-primary border-0 h-14"
                onClick={() => navigate('/add-property?listing_type=sale')}
              >
                Sell a Property
              </Button>
              <Button
                data-testid="action-rent-out"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-primary border-0 h-14"
                onClick={() => navigate('/add-property?listing_type=rent')}
              >
                Put Property for Rent
              </Button>
              <Button
                data-testid="action-find-rent"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-primary border-0 h-14"
                onClick={() => navigate('/properties?listing_type=rent')}
              >
                Find Property to Rent
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white" data-testid="features-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3" data-testid="features-heading">
              Why Choose The True Bridge?
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Your trusted partner in Kerala's real estate journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:border-primary/20 transition-all duration-300" data-testid="feature-verified">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Verified Properties</h3>
              <p className="text-sm leading-relaxed text-slate-600">
                Every property is thoroughly verified to ensure authenticity and legal compliance.
              </p>
            </div>

            <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:border-primary/20 transition-all duration-300" data-testid="feature-financing">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Easy Financing</h3>
              <p className="text-sm leading-relaxed text-slate-600">
                Get instant EMI calculations and financing options tailored for you.
              </p>
            </div>

            <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:border-primary/20 transition-all duration-300" data-testid="feature-support">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">24/7 Support</h3>
              <p className="text-sm leading-relaxed text-slate-600">
                Our dedicated team is always ready to assist you throughout your journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* New Launches Section */}
      {builderProjects.length > 0 && (
        <section className="section-padding bg-slate-50" data-testid="new-launches-section">
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex justify-between items-end mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" data-testid="new-launches-heading">
                  New Launches from Top Builders
                </h2>
                <p className="text-base text-slate-600">Exclusive projects from Kerala's most trusted developers</p>
              </div>
              <Button
                data-testid="view-all-launches"
                variant="outline"
                onClick={() => navigate('/new-launches')}
                className="hidden md:block border-primary/20 hover:bg-primary/5"
              >
                View All Launches
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {builderProjects.map((project) => (
                <div
                  key={project.project_id}
                  data-testid={`builder-project-${project.project_id}`}
                  className="group bg-white rounded-lg overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.images?.[0] || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00'}
                      alt={project.project_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-primary text-white text-xs font-medium rounded">
                        {project.builder_name}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-0.5 bg-secondary text-white text-xs font-medium rounded">
                        NEW
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold mb-1.5 line-clamp-1">{project.project_name}</h3>
                    <div className="flex items-center text-slate-600 mb-2">
                      <MapPin className="w-3.5 h-3.5 mr-1" />
                      <span className="text-sm">{project.location?.district}, Kerala</span>
                    </div>
                    {project.configurations && project.configurations.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {project.configurations.slice(0, 3).map((config, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs">
                            {config}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500">Starting from</p>
                        <p className="text-lg font-semibold text-primary">
                          ₹{(project.price_range?.min / 10000000).toFixed(1)}Cr
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate('/new-launches')}
                        className="bg-primary hover:bg-primary/90 text-white text-sm"
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8 md:hidden">
              <Button
                data-testid="view-all-launches-mobile"
                variant="outline"
                onClick={() => navigate('/new-launches')}
                className="border-primary/20 hover:bg-primary/5"
              >
                View All New Launches
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Properties */}
      <section className="section-padding bg-background" data-testid="featured-properties-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="featured-heading">
                Featured Properties
              </h2>
              <p className="text-lg text-slate-600">Discover the best properties across Kerala</p>
            </div>
            <Button
              data-testid="view-all-properties"
              variant="outline"
              onClick={() => navigate('/properties')}
              className="hidden md:block border-primary/20 hover:bg-primary/5"
            >
              View All
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredProperties.map((property) => (
              <div
                key={property.property_id}
                data-testid={`property-card-${property.property_id}`}
                className="property-card group relative bg-white rounded-lg overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/properties/${property.property_id}`)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={property.images?.[0] || 'https://images.unsplash.com/photo-1570709073200-5c34c5fa8688'}
                    alt={property.title}
                    className="property-card-image w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-primary text-white text-sm font-medium rounded-md">
                      {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 line-clamp-1">{property.title}</h3>
                  <div className="flex items-center text-slate-600 mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">{property.location?.district}, Kerala</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-semibold text-primary">
                      ₹{property.price?.toLocaleString('en-IN')}
                    </span>
                    <div className="flex gap-3 text-sm text-slate-600">
                      {property.bedrooms && (
                        <span>{property.bedrooms} BHK</span>
                      )}
                      {property.area && (
                        <span>{property.area} sq.ft</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:hidden">
            <Button
              data-testid="view-all-properties-mobile"
              variant="outline"
              onClick={() => navigate('/properties')}
              className="border-primary/20 hover:bg-primary/5"
            >
              View All Properties
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary" data-testid="cta-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-white">
            Ready to Find Your Dream Property?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who found their perfect property with us.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              data-testid="cta-explore"
              onClick={() => navigate('/properties')}
              className="h-14 px-8 bg-white text-primary hover:bg-white/90 font-medium shadow-lg"
            >
              Explore Properties
            </Button>
            <Button
              data-testid="cta-calculator"
              onClick={() => navigate('/calculator')}
              variant="outline"
              className="h-14 px-8 bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-medium"
            >
              EMI Calculator
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-heading font-bold mb-4">True Bridge</h3>
              <p className="text-slate-400 text-sm mb-2">Collaboration. Innovation. Trust.</p>
              <p className="text-slate-400 text-sm">Your trusted real estate partner in Kerala</p>
              <p className="text-slate-400 text-sm mt-2">www.thetruebridge.com</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/properties" className="hover:text-white transition-colors">Properties</a></li>
                <li><a href="/new-launches" className="hover:text-white transition-colors">New Launches</a></li>
                <li><a href="/calculator" className="hover:text-white transition-colors">EMI Calculator</a></li>
                <li><a href="/dashboard" className="hover:text-white transition-colors">Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Property Types</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/properties?property_type=residential" className="hover:text-white transition-colors">Residential</a></li>
                <li><a href="/properties?property_type=commercial" className="hover:text-white transition-colors">Commercial</a></li>
                <li><a href="/properties?listing_type=rent" className="hover:text-white transition-colors">Rentals</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>TBIFSL, 1st Floor, Ruby Court View Apartment</li>
                <li>ERG Road, Ernakulam. 682018</li>
                <li>Mobile: +91 81119 54355</li>
                <li>Email: abhilash@thetruebridge.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© 2026 True Bridge. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}